const U = (id, w = 1600) => `https://images.unsplash.com/${id}?q=85&w=${w}`;

const COMMODITY_TOPICS = {
  'lithium': {
    title: 'Lithium', category: 'Minerals', accent: '#00CED1',
    tagline: "Powering the global energy transition from Africa's richest deposits",
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/0b23b238-d9a7-4a7a-8c18-f58cb151cea3/images/c917a7a152f716599624e3d2616939492ddc22bcaba7c8f08e81ac752b8666ea.png',
    overview: [
      "Zimbabwe holds Africa's largest lithium reserves and ranks among the top producers globally. Through Crystal Clear Corp, Xcelsior structures ethical, fully traceable lithium value chains — from spodumene and petalite extraction to concentrate export — using solar-powered processing that meets the EU's CBAM and international ESG benchmarks.",
      "We connect vetted producers with battery manufacturers, cathode plants and commodity houses across Asia, Europe and the Middle East, providing assay verification, logistics management and transparent blockchain-tracked provenance for every shipment."
    ],
    highlights: [
      { t: 'Ethical Extraction', d: 'Solar-powered processing with community benefit-sharing agreements' },
      { t: 'Blockchain Provenance', d: 'Every consignment digitally tracked from pit to port' },
      { t: 'Assay & QC', d: 'Independent laboratory verification of grade and moisture' },
      { t: 'Export Logistics', d: 'End-to-end freight via Beira, Durban and Walvis Bay corridors' }
    ],
    stats: [
      { value: '#1', label: 'Lithium reserves in Africa (Zimbabwe)' },
      { value: 'CBAM', label: 'EU compliance-ready value chains' },
      { value: '100%', label: 'Traceable consignments' }
    ],
    initiative: { title: 'Solar-Powered Beneficiation', text: 'Crystal Clear Corp is developing in-country beneficiation powered by dedicated solar capacity — capturing more value locally while keeping the carbon footprint near zero.' },
    related: ['gold', 'platinum', 'blockchain-traceability']
  },
  'gold': {
    title: 'Gold', category: 'Minerals', accent: '#FF8C00',
    tagline: 'Responsible bullion and doré sourcing with full chain-of-custody',
    heroImage: U('photo-1610375461246-83df859d849d'),
    overview: [
      "Gold remains Zimbabwe's single largest export earner. Xcelsior aggregates production from licensed small-scale and primary producers, ensuring every ounce is conflict-free, legally exported through official channels and accompanied by complete chain-of-custody documentation.",
      "Our trading desk offers refiners, banks and private buyers reliable allocation with independent assay, secure vaulted logistics and pricing benchmarked to LBMA standards."
    ],
    highlights: [
      { t: 'Licensed Supply', d: 'Aggregation exclusively from registered, compliant producers' },
      { t: 'LBMA-Benchmarked Pricing', d: 'Transparent pricing referenced to global benchmarks' },
      { t: 'Secure Logistics', d: 'Insured, vaulted transport and handover protocols' },
      { t: 'Chain of Custody', d: 'Full documentation from mine to refinery' }
    ],
    stats: [
      { value: '35t+', label: 'Zimbabwe annual gold output' },
      { value: '#1', label: 'Export earner nationally' },
      { value: '0', label: 'Tolerance for undocumented supply' }
    ],
    initiative: { title: 'Formalising Artisanal Supply', text: 'We partner with cooperatives to bring artisanal miners into formal, safer and better-paid value chains — expanding compliant supply while uplifting communities.' },
    related: ['lithium', 'platinum', 'chrome']
  },
  'platinum': {
    title: 'Platinum', category: 'Minerals', accent: '#00CED1',
    tagline: 'PGMs from the Great Dyke — the second-largest platinum reserves on earth',
    heroImage: U('photo-1518770660439-4636190af475'),
    overview: [
      "Zimbabwe's Great Dyke hosts the world's second-largest platinum group metal (PGM) reserves after South Africa's Bushveld Complex. Xcelsior facilitates offtake and trading of platinum, palladium and rhodium concentrates for catalytic, industrial and investment applications.",
      "With hydrogen fuel-cell demand accelerating, our PGM desk positions partners at the centre of the clean-energy materials economy, with ESG-audited supply and structured long-term offtake agreements."
    ],
    highlights: [
      { t: 'Great Dyke Access', d: 'Relationships across the premier PGM geology outside South Africa' },
      { t: 'Structured Offtake', d: 'Long-term agreements for concentrate and matte' },
      { t: 'Hydrogen Economy Ready', d: 'Positioning supply for fuel-cell catalyst demand' },
      { t: 'ESG-Audited', d: 'Environmental and labour compliance verified' }
    ],
    stats: [
      { value: '2nd', label: 'Largest PGM reserves globally' },
      { value: '3', label: 'Metals traded: Pt, Pd, Rh' },
      { value: '550km', label: 'Great Dyke mineral corridor' }
    ],
    initiative: { title: 'PGM Value Addition', text: 'Supporting national policy on in-country refining, we are structuring investment vehicles for local smelting and base-metal refinery capacity.' },
    related: ['lithium', 'gold', 'nickel']
  },
  'chrome': {
    title: 'Chrome', category: 'Minerals', accent: '#FF8C00',
    tagline: 'High-grade chromite for the global ferrochrome and stainless steel industry',
    heroImage: 'https://static.prod-images.emergentagent.com/jobs/0b23b238-d9a7-4a7a-8c18-f58cb151cea3/images/c917a7a152f716599624e3d2616939492ddc22bcaba7c8f08e81ac752b8666ea.png',
    overview: [
      "Zimbabwe holds an estimated 12% of the world's chromite reserves, concentrated along the Great Dyke. Xcelsior sources lumpy and fine chrome ore of metallurgical grade (38–52% Cr2O3) from compliant producers for export to ferrochrome smelters in Asia and Europe.",
      "Our team manages sampling, grade blending, bagging and multimodal logistics, providing buyers with consistent specification and dependable monthly volumes."
    ],
    highlights: [
      { t: 'Metallurgical Grade', d: '38–52% Cr2O3 lumpy and concentrate supply' },
      { t: 'Grade Blending', d: 'Consistent specification through managed stockpiles' },
      { t: 'Monthly Volumes', d: 'Contracted, dependable shipment schedules' },
      { t: 'Multimodal Logistics', d: 'Road, rail and seafreight coordination' }
    ],
    stats: [
      { value: '12%', label: 'Of global chromite reserves' },
      { value: '52%', label: 'Peak Cr2O3 grades available' },
      { value: '1000s', label: 'Of tonnes shipped monthly' }
    ],
    initiative: { title: 'Ferrochrome Partnerships', text: 'We are structuring joint ventures for local ferrochrome smelting to move Zimbabwe up the stainless-steel value chain.' },
    related: ['nickel', 'platinum', 'lithium']
  },
  'nickel': {
    title: 'Nickel', category: 'Minerals', accent: '#00CED1',
    tagline: 'Battery-grade nickel for the electric mobility revolution',
    heroImage: U('photo-1518770660439-4636190af475'),
    overview: [
      "Nickel is indispensable to EV battery cathodes and stainless steel. Xcelsior facilitates trade in nickel concentrates and matte from Zimbabwean and regional producers, connecting supply with battery-precursor plants and refineries worldwide.",
      "As our Pan-African EV Network scales, our nickel desk closes the loop — the same metal we trade powers the charging infrastructure and fleets we deploy."
    ],
    highlights: [
      { t: 'Battery-Grade Focus', d: 'Class 1 nickel pathways for EV cathode supply' },
      { t: 'Regional Aggregation', d: 'Zimbabwe, Zambia and Tanzania sourcing network' },
      { t: 'Refinery Relationships', d: 'Direct lines to Asian and European refiners' },
      { t: 'Circular Strategy', d: 'Feeding the same EV ecosystem we are building' }
    ],
    stats: [
      { value: 'Class 1', label: 'Battery-grade pathways' },
      { value: '3', label: 'Sourcing countries' },
      { value: '2027', label: 'EV network integration target' }
    ],
    initiative: { title: 'Mine-to-Battery Corridor', text: 'Structuring a traceable nickel corridor that links African mines directly to global battery gigafactories under ESG-audited terms.' },
    related: ['lithium', 'chrome', 'ev-network']
  },
  'non-gmo-maize': {
    title: 'Non-GMO Maize', category: 'Agriproducts', accent: '#FF8C00',
    tagline: "Africa's staple grain, grown clean and traded transparently",
    heroImage: U('photo-1551754655-cd27e38d2076'),
    overview: [
      "Zimbabwe maintains a strict non-GMO agricultural policy, making its white and yellow maize highly sought after in premium food and feed markets. Through Jordan Crossover, Xcelsior aggregates certified non-GMO maize from commercial and smallholder farmers — including our veteran empowerment plots.",
      "Every consignment is moisture-tested, aflatoxin-screened and shipped with full phytosanitary documentation, with blockchain records tracing grain from field to buyer."
    ],
    highlights: [
      { t: 'Certified Non-GMO', d: 'National policy-backed GMO-free guarantee' },
      { t: 'Quality Screened', d: 'Moisture, grading and aflatoxin testing on every lot' },
      { t: 'Smallholder Inclusion', d: 'Veteran and community farmers integrated into export chains' },
      { t: 'Bulk & Bagged', d: 'Flexible packaging for regional and overseas buyers' }
    ],
    stats: [
      { value: '100%', label: 'Non-GMO certified' },
      { value: '12,750', label: 'Veteran plots feeding supply' },
      { value: 'GrainSA', label: 'Grading standards applied' }
    ],
    initiative: { title: 'Solar-Irrigated Production', text: 'Our veteran agriculture program grows maize on solar-irrigated plots — climate-resilient supply that is both ethical and dependable.' },
    related: ['sunflower', 'tobacco', 'veteran-empowerment']
  },
  'sunflower': {
    title: 'Sunflower', category: 'Agriproducts', accent: '#00CED1',
    tagline: 'High-oleic seed and crude oil for global edible-oil markets',
    heroImage: U('photo-1470509037663-253afd7f0f51'),
    overview: [
      "Sunflower is a fast-growing pillar of Zimbabwe's oilseed strategy. Xcelsior contracts production of high-oleic sunflower seed with smallholder clusters and commercial growers, supplying crushers and exporting seed, cake and crude oil.",
      "Our climate-smart agronomy support — drought-tolerant varieties, solar irrigation and guaranteed offtake — gives farmers income security while giving buyers dependable volume."
    ],
    highlights: [
      { t: 'Contract Farming', d: 'Guaranteed offtake with agronomy support' },
      { t: 'High-Oleic Varieties', d: 'Premium oil profiles for food manufacturers' },
      { t: 'Full Value Chain', d: 'Seed, cake and crude oil supply' },
      { t: 'Climate-Smart', d: 'Drought-tolerant seed and solar irrigation' }
    ],
    stats: [
      { value: '3', label: 'Products: seed, cake, oil' },
      { value: '40%+', label: 'Oil content varieties' },
      { value: '2 seasons', label: 'Per year under irrigation' }
    ],
    initiative: { title: 'Oilseed Expansion Program', text: 'Scaling contracted sunflower hectarage year-on-year to substitute edible-oil imports and open export lines to the region.' },
    related: ['non-gmo-maize', 'coffee', 'veteran-empowerment']
  },
  'tobacco': {
    title: 'Tobacco', category: 'Agriproducts', accent: '#FF8C00',
    tagline: "Zimbabwe's golden leaf — the world's finest flue-cured Virginia",
    heroImage: U('photo-1500937386664-56d1dfef3854'),
    overview: [
      "Zimbabwe is the world's fourth-largest producer of flue-cured Virginia tobacco, prized for its flavour and style. Xcelsior facilitates fully licensed leaf export — from grower flooring through TIMB-regulated auction and contract systems to international merchants.",
      "We emphasise sustainable production: rocket barns, woodlots and solar curing that reduce deforestation, alongside farmer development programs that raise quality and yields."
    ],
    highlights: [
      { t: 'TIMB Licensed', d: 'Fully regulated export channels' },
      { t: 'Premium Styles', d: 'Flue-cured Virginia across all grades' },
      { t: 'Sustainable Curing', d: 'Solar and efficient-barn programs cutting wood use' },
      { t: 'Grower Development', d: 'Inputs, financing and training partnerships' }
    ],
    stats: [
      { value: '4th', label: 'Largest FCV producer globally' },
      { value: '200M+ kg', label: 'National seasonal output' },
      { value: '100%', label: 'Licensed & documented exports' }
    ],
    initiative: { title: 'Green Curing Transition', text: 'Rolling out solar-assisted curing to cut the carbon and forest footprint of every kilogram exported.' },
    related: ['non-gmo-maize', 'tea', 'esg-standard']
  },
  'coffee': {
    title: 'Coffee', category: 'Agriproducts', accent: '#00CED1',
    tagline: 'Speciality Arabica from the Eastern Highlands, reborn for the world',
    heroImage: U('photo-1447933601403-0c6688de566e'),
    overview: [
      "Zimbabwe's Eastern Highlands produce some of Africa's most distinctive speciality Arabica — bright, fruity and consistently cupping above 84 points. Xcelsior is part of the renaissance of this once-famous origin, linking estates and smallholder cooperatives to speciality roasters worldwide.",
      "We manage wet-mill quality, grading, export documentation and direct-trade relationships that return a premium to growers and full traceability to roasters."
    ],
    highlights: [
      { t: 'Speciality Grade', d: '84+ cupping scores from high-altitude estates' },
      { t: 'Direct Trade', d: 'Farm-to-roaster relationships with premium pricing' },
      { t: 'Traceable Lots', d: 'Single-estate and micro-lot documentation' },
      { t: 'Origin Revival', d: 'Rebuilding a legendary African coffee origin' }
    ],
    stats: [
      { value: '84+', label: 'Typical cupping scores' },
      { value: '1,600m+', label: 'Growing altitude' },
      { value: 'AA/AB', label: 'Export grades offered' }
    ],
    initiative: { title: 'Highlands Coffee Revival', text: 'Partnering with cooperatives in Honde Valley and Chipinge to replant, process and market Zimbabwean speciality coffee globally.' },
    related: ['tea', 'sunflower', 'blockchain-traceability']
  },
  'tea': {
    title: 'Tea', category: 'Agriproducts', accent: '#FF8C00',
    tagline: 'Estate-grown black and speciality teas from misty highland gardens',
    heroImage: U('photo-1564890369478-c89ca6d9cde9'),
    overview: [
      "The high-rainfall Eastern Highlands host mature tea estates producing quality black CTC and orthodox teas. Xcelsior trades bulk and value-added tea, connecting estates with blenders and packers in the Middle East, Europe and Asia.",
      "Our value-addition agenda includes local packing, flavoured lines and organic conversion programs that raise export earnings per kilogram."
    ],
    highlights: [
      { t: 'Estate Sourced', d: 'Direct relationships with established highland gardens' },
      { t: 'CTC & Orthodox', d: 'Full range of black tea manufacture' },
      { t: 'Value Addition', d: 'Local blending and packing programs' },
      { t: 'Organic Pathways', d: 'Certification support for premium markets' }
    ],
    stats: [
      { value: '20M+ kg', label: 'National annual production' },
      { value: '2', label: 'Manufacture types: CTC, orthodox' },
      { value: '3', label: 'Continents served' }
    ],
    initiative: { title: 'Pack-at-Origin Program', text: 'Shifting exports from bulk to branded, packed-at-origin tea to multiply the value retained in Africa.' },
    related: ['coffee', 'tobacco', 'esg-standard']
  },
  'ppe': {
    title: 'PPE', category: 'Medical Supplies', accent: '#00CED1',
    tagline: 'Certified personal protective equipment, delivered at scale',
    heroImage: U('photo-1584634731339-252c581abfc5'),
    overview: [
      "Born during COVID-19, Xcelsior's medical supply desk was forged in the world's toughest procurement environment. We source and deliver certified PPE — masks, respirators, gowns, gloves, face shields and sanitisation products — for governments, hospitals and mining houses.",
      "Every product line carries CE, FDA or SAHPRA-aligned certification with batch documentation, and our logistics network handles cold-chain and time-critical delivery across the region."
    ],
    highlights: [
      { t: 'Certified Lines', d: 'CE / FDA / SAHPRA-aligned documentation' },
      { t: 'Government Scale', d: 'Tender-ready volumes and compliance packs' },
      { t: 'Rapid Deployment', d: 'Air and road freight for urgent requirements' },
      { t: 'Verified Factories', d: 'Audited manufacturers only' }
    ],
    stats: [
      { value: '2020', label: 'Desk founded during COVID-19' },
      { value: '6+', label: 'PPE product categories' },
      { value: '48hr', label: 'Emergency quotation turnaround' }
    ],
    initiative: { title: 'Pandemic Preparedness Stockpiles', text: 'Advising institutions on rotating strategic PPE stockpiles so the region is never caught unprepared again.' },
    related: ['pharmaceuticals', 'medical-equipment', 'diagnostic-tools']
  },
  'pharmaceuticals': {
    title: 'Pharmaceuticals', category: 'Medical Supplies', accent: '#FF8C00',
    tagline: 'Essential medicines sourced through WHO-aligned channels',
    heroImage: U('photo-1585435557343-3b092031a831'),
    overview: [
      "Xcelsior facilitates the procurement and distribution of essential medicines — antibiotics, antiretrovirals, antimalarials, chronic-care and critical-care lines — working exclusively with WHO-prequalified and stringently regulated manufacturers.",
      "Our pharma logistics maintain GDP-compliant cold chains and full batch traceability, serving ministries of health, hospital groups, NGOs and private pharmacy networks."
    ],
    highlights: [
      { t: 'WHO-Prequalified', d: 'Sourcing only from qualified manufacturers' },
      { t: 'Cold Chain', d: 'GDP-compliant temperature-controlled logistics' },
      { t: 'Essential Lists', d: 'Coverage of national essential medicine lists' },
      { t: 'Batch Traceability', d: 'Serialised tracking against falsified medicines' }
    ],
    stats: [
      { value: 'GDP', label: 'Compliant distribution' },
      { value: '5+', label: 'Therapeutic categories' },
      { value: '100%', label: 'Batch documentation' }
    ],
    initiative: { title: 'Zoe Life Health & Wellness', text: "Our Zoe Life division is developing ethically sourced natural pharmaceutical and cosmetic lines — African wellness products for global shelves." },
    related: ['ppe', 'diagnostic-tools', 'telemedicine-hubs']
  },
  'medical-equipment': {
    title: 'Medical Equipment', category: 'Medical Supplies', accent: '#00CED1',
    tagline: 'Hospital-grade equipment with installation, training and support',
    heroImage: U('photo-1576091160399-112ba8d25d1d'),
    overview: [
      "From imaging and theatre equipment to ICU beds, ventilators and laboratory instruments, Xcelsior supplies hospital-grade medical equipment with a full lifecycle service: specification, importation, installation, biomedical training and maintenance contracts.",
      "We partner with leading OEMs and structure financing — including lease-to-own and DFI-backed facilities — so health institutions can modernise without prohibitive upfront capital."
    ],
    highlights: [
      { t: 'Full Lifecycle', d: 'Supply, install, train and maintain' },
      { t: 'OEM Partnerships', d: 'Authorised channels with warranty protection' },
      { t: 'Equipment Financing', d: 'Lease-to-own and DFI-backed structures' },
      { t: 'Biomedical Training', d: 'Local capacity to run and repair equipment' }
    ],
    stats: [
      { value: '10+', label: 'Equipment categories' },
      { value: '24/7', label: 'Service support model' },
      { value: 'OEM', label: 'Authorised sourcing only' }
    ],
    initiative: { title: 'Telemedicine Hub Equipment', text: 'Outfitting our telemedicine clinics with diagnostic and monitoring equipment that connects rural patients to specialists.' },
    related: ['telemedicine-hubs', 'diagnostic-tools', 'ppe']
  },
  'diagnostic-tools': {
    title: 'Diagnostic Tools', category: 'Medical Supplies', accent: '#FF8C00',
    tagline: 'Point-of-care and laboratory diagnostics for early detection',
    heroImage: U('photo-1579154204601-01588f351e67'),
    overview: [
      "Early diagnosis saves lives and health budgets. Xcelsior supplies rapid test kits, point-of-care analysers, laboratory reagents and imaging diagnostics covering infectious disease, maternal health and chronic-condition screening.",
      "We integrate diagnostics into our telemedicine hub network, enabling AI-assisted triage in communities that have never had laboratory access."
    ],
    highlights: [
      { t: 'Point-of-Care', d: 'Rapid results where patients live' },
      { t: 'Lab Reagents', d: 'Consistent consumable supply lines' },
      { t: 'AI-Assisted Triage', d: 'Digital tools amplifying scarce clinicians' },
      { t: 'Quality Assured', d: 'WHO-aligned performance standards' }
    ],
    stats: [
      { value: '15min', label: 'Rapid test turnaround' },
      { value: '3', label: 'Screening focus areas' },
      { value: 'AI', label: 'Triage integration via XAINE' }
    ],
    initiative: { title: 'Community Screening Drives', text: 'Deploying mobile diagnostic units alongside our impact programs to screen thousands in underserved districts.' },
    related: ['medical-equipment', 'telemedicine-hubs', 'ai-logistics']
  },
  'security-equipment': {
    title: 'Security Equipment', category: 'Strategic Supplies', accent: '#FF8C00',
    tagline: 'Protective and surveillance systems for national and industrial security',
    heroImage: U('photo-1557597774-9d273605dfa9'),
    overview: [
      "Xcelsior supplies non-lethal security and protection equipment to government agencies, mines and critical infrastructure operators: perimeter surveillance, access control, CCTV and command-centre systems, protective barriers and screening technology.",
      "All engagements follow strict end-user certification and international compliance protocols, with full training and maintenance support built into every contract."
    ],
    highlights: [
      { t: 'Perimeter & CCTV', d: 'Integrated surveillance and monitoring systems' },
      { t: 'Access Control', d: 'Biometric and smart-card infrastructure' },
      { t: 'Compliance First', d: 'End-user certificates and export-control adherence' },
      { t: 'Turnkey Delivery', d: 'Design, install, train and support' }
    ],
    stats: [
      { value: '100%', label: 'End-user certified contracts' },
      { value: '24/7', label: 'Command-centre capability' },
      { value: 'Tier-1', label: 'Manufacturer partnerships' }
    ],
    initiative: { title: 'Critical Infrastructure Protection', text: 'Securing the solar farms, substations and logistics corridors that our own divisions are building across the region.' },
    related: ['tactical-gear', 'communication-systems', 'defence-solar']
  },
  'tactical-gear': {
    title: 'Tactical Gear', category: 'Strategic Supplies', accent: '#00CED1',
    tagline: 'Certified protective gear for defence, ranger and security services',
    heroImage: U('photo-1595590424283-b8f17842773f'),
    overview: [
      "We supply certified tactical and protective gear — body armour, helmets, boots, uniforms, load-bearing systems and field equipment — to defence forces, wildlife ranger units and licensed security operators.",
      "Sourcing follows NATO-standard specifications with rigorous end-user documentation, and our anti-poaching partnerships equip the rangers protecting Africa's natural heritage."
    ],
    highlights: [
      { t: 'NIJ/NATO Standards', d: 'Certified ballistic and protective ratings' },
      { t: 'Ranger Programs', d: 'Equipping anti-poaching units across the region' },
      { t: 'Full Kit Solutions', d: 'Head-to-toe individual equipment programs' },
      { t: 'Compliant Channels', d: 'Strict end-user and export documentation' }
    ],
    stats: [
      { value: 'NIJ', label: 'Ballistic certification standard' },
      { value: '100%', label: 'Documented end-users' },
      { value: '5+', label: 'Equipment categories' }
    ],
    initiative: { title: 'Ranger Support Initiative', text: 'Subsidised gear packages for wildlife ranger units, protecting both biodiversity and the communities that depend on it.' },
    related: ['security-equipment', 'communication-systems', 'esg-standard']
  },
  'communication-systems': {
    title: 'Communication Systems', category: 'Strategic Supplies', accent: '#FF8C00',
    tagline: 'Secure, resilient communications for mission-critical operations',
    heroImage: U('photo-1558494949-ef010cbdcc31'),
    overview: [
      "Reliable communication is the backbone of security, logistics and emergency response. Xcelsior supplies and integrates secure radio networks, satellite connectivity, encrypted communication platforms and command-and-control systems.",
      "Our solutions serve defence and public-safety agencies as well as mines and logistics fleets operating beyond cellular coverage — engineered for African distances and conditions."
    ],
    highlights: [
      { t: 'Secure Radio Networks', d: 'Digital trunked and encrypted VHF/UHF systems' },
      { t: 'Satellite Connectivity', d: 'VSAT and LEO solutions for remote operations' },
      { t: 'Command & Control', d: 'Integrated dispatch and situational awareness' },
      { t: 'Fleet Communications', d: 'Tracking and comms for logistics corridors' }
    ],
    stats: [
      { value: 'AES-256', label: 'Encryption standard' },
      { value: 'LEO+GEO', label: 'Satellite coverage options' },
      { value: '100%', label: 'Coverage beyond cellular' }
    ],
    initiative: { title: 'Connected Corridors', text: 'Building communication backbones along our EV and logistics corridors so every vehicle and station stays connected.' },
    related: ['security-equipment', 'tactical-gear', 'ai-logistics']
  }
};

export default COMMODITY_TOPICS;
